import { Patterns } from "../../../../constants/patterns";
import type { DetectedLinksMap } from "../../../core/sns-detector";
import { snsPreviewManagerForm } from "../../sns-preview-manager-form";

export class SnsCardPreviewIconsRender {
    private static getOrCreateMediaOverlay(cardElement: HTMLElement): HTMLElement | null {
        const boardMediaArea = snsPreviewManagerForm.getBoardMediaArea(cardElement);
        if (!(boardMediaArea instanceof HTMLElement)) return null;

        const existingOverlay = snsPreviewManagerForm.getBoardFooterSns(boardMediaArea);
        if (existingOverlay instanceof HTMLElement) {
            existingOverlay.replaceChildren();
            return existingOverlay;
        }

        // When the placeholder is still in its initial loading state (no tiles created yet),
        // skip creating the floating overlay so the placeholderIcon path can replace the
        // loading animation with the platform icon directly inside the placeholder.
        const placeholder = snsPreviewManagerForm.getBoardMediaPlaceholder(boardMediaArea);
        if (
            placeholder instanceof HTMLElement &&
            snsPreviewManagerForm.getPlaceholderLoadingText(placeholder) instanceof HTMLElement
        ) {
            return null;
        }

        const overlay = document.createElement("div");
        overlay.className = "board-footer-sns board-media-sns";
        overlay.setAttribute("aria-label", "Post SNS");
        boardMediaArea.appendChild(overlay);
        return overlay;
    }

    public static createPlatformBadge(platform: string): HTMLElement | null {
        const platformConfig = Patterns.SNS_PLATFORMS[platform];
        if (!platformConfig) return null;

        const badge = document.createElement("div");
        badge.className = "sns-preview-tile__platform-badge";
        badge.setAttribute("aria-label", platformConfig.name);
        badge.title = platformConfig.name;

        const iconElement = document.createElement("i");
        iconElement.className = `bi ${platformConfig.icon} sns-icon`;
        iconElement.style.color = platformConfig.color;
        iconElement.setAttribute("aria-hidden", "true");

        badge.appendChild(iconElement);
        return badge;
    }

    public static appendPlatformIcons(iconsContainer: HTMLElement, detectedLinks: DetectedLinksMap): void {
        Object.keys(detectedLinks || {}).forEach((platform) => {
            const platformConfig = Patterns.SNS_PLATFORMS[platform];
            if (!platformConfig) return;

            const iconElement = document.createElement("i");
            iconElement.className = `bi ${platformConfig.icon} sns-icon`;
            iconElement.style.cssText = `
                color: ${platformConfig.color};
                font-size: 14px;
                opacity: 0.8;
            `;
            iconElement.title = `${platformConfig.name} content detected`;

            iconsContainer.appendChild(iconElement);
        });
    }

    public static replacePlaceholderIconWithPlatformIcons(
        placeholderIcon: HTMLElement,
        detectedLinks: DetectedLinksMap,
    ): void {
        placeholderIcon.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
            flex-wrap: wrap;
            min-height: 40px;
            padding: 4px;
            box-sizing: border-box;
        `;
        placeholderIcon.innerHTML = "";

        Object.keys(detectedLinks || {}).forEach((platform) => {
            const platformConfig = Patterns.SNS_PLATFORMS[platform];
            if (!platformConfig) return;

            const iconElement = document.createElement("i");
            iconElement.className = `bi ${platformConfig.icon} sns-icon`;
            iconElement.style.cssText = `
                color: ${platformConfig.color};
                font-size: 18px;
                opacity: 0.9;
                margin: 2px;
            `;
            iconElement.title = `${platformConfig.name} content detected`;

            placeholderIcon.appendChild(iconElement);
        });
    }

    public static renderSnsIconsOnCard(cardElement: HTMLElement, detectedLinks: DetectedLinksMap): void {
        snsPreviewManagerForm.getBoardFooterSnsList(cardElement).forEach((element) => element.remove());
        // Only remove platform badges from SNS tiles (those with data-sns-platform).
        // Image tiles have no data-sns-platform and keep their SSR-rendered camera badge.
        snsPreviewManagerForm
            .getTileUrlElements(cardElement)
            .filter((tile) => !!tile.dataset.snsPlatform)
            .forEach((tile) => snsPreviewManagerForm.getPlatformBadges(tile).forEach((el) => el.remove()));

        const tiles = snsPreviewManagerForm
            .getTileUrlElements(cardElement)
            .filter((tile) => tile.dataset.snsPlatform && Patterns.SNS_PLATFORMS[tile.dataset.snsPlatform]);

        if (tiles.length > 0) {
            tiles.forEach((tile) => {
                const platform = tile.dataset.snsPlatform;
                if (!platform) return;

                const badge = this.createPlatformBadge(platform);
                if (!(badge instanceof HTMLElement)) return;
                tile.appendChild(badge);
            });

            return;
        }

        const existingIcons = snsPreviewManagerForm.getExistingIcons(cardElement);
        if (existingIcons) {
            existingIcons.remove();
        }

        if (Object.keys(detectedLinks || {}).length === 0) return;

        const iconsContainer = document.createElement("div");
        iconsContainer.className = "sns-icons";
        iconsContainer.style.cssText = `
            display: flex;
            gap: 4px;
            margin-top: 6px;
            margin-bottom: 4px;
            flex-wrap: wrap;
            max-width: 100%;
            box-sizing: border-box;
        `;

        this.appendPlatformIcons(iconsContainer, detectedLinks);

        const boardActions = snsPreviewManagerForm.getBoardActions(cardElement);
        const placeholderIcon = snsPreviewManagerForm.getPlainPlaceholderIcon(cardElement);
        const placeholderText = snsPreviewManagerForm.getPlainPlaceholderText(cardElement);
        const mediaOverlay = this.getOrCreateMediaOverlay(cardElement);

        if (mediaOverlay instanceof HTMLElement) {
            iconsContainer.style.cssText = `
                display: flex;
                gap: 4px;
                align-items: center;
                box-sizing: border-box;
            `;
            mediaOverlay.appendChild(iconsContainer);
            return;
        }

        if (boardActions instanceof HTMLElement) {
            return;
        }

        if (placeholderIcon instanceof HTMLElement) {
            this.replacePlaceholderIconWithPlatformIcons(placeholderIcon, detectedLinks);

            if (placeholderText instanceof HTMLElement) {
                const platformNames = Object.keys(detectedLinks).map((platform) => {
                    const config = Patterns.SNS_PLATFORMS[platform];
                    return config ? config.name : platform;
                });
                placeholderText.textContent = platformNames.join(" + ") + " CONTENT";
                placeholderText.style.cssText = `
                    font-size: 10px;
                    color: #666;
                    text-transform: uppercase;
                    margin-top: 2px;
                `;
            }
            return;
        }

        cardElement.appendChild(iconsContainer);
    }
}
